import React, { useEffect, useRef, useState } from 'react'
import { fromEvent, merge } from 'rxjs'
import { debounceTime, map } from 'rxjs/operators'
import { Popover, cn, makeStyles } from '@cicero/ui'
import { EditorService, IEditorElementProps, IElementDefinition } from '../EditorService'
import { UserTile } from 'user'
import { IMentionElement } from '../commands'

export const MentionElement = ({ children, attributes, element }: IEditorElementProps<IMentionElement>) => {
  const { mentionWrapper, mentionPopover, mentionFocused } = useStyles()
  const [open, setOpen] = useState(false)
  const selected = EditorService.useSelected()
  const focused = EditorService.useFocused()
  const anchor = useRef<HTMLElement>()

  useEffect(() => {
    if (!anchor.current) { return }
    const hovering$ = merge(
      fromEvent(anchor.current, 'mouseover').pipe(map(() => true)),
      fromEvent(anchor.current, 'mouseout').pipe(map(() => false))
    ).pipe(debounceTime(1000))

    const sub = hovering$.subscribe((visible) => setOpen(visible))
    return () => sub.unsubscribe()
  }, [anchor.current])

  return <span
    {...attributes}
    className={cn(mentionWrapper, { [mentionFocused]: selected && focused })}
    contentEditable={false}
    ref={anchor}
  >
    <span>{children}</span>
    <span>{element.name}</span>
    <Popover
      className={mentionPopover}
      open={open}
      anchorEl={anchor.current}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <UserTile id={element.user_id} />
    </Popover>
  </span>
}

export const MentionElementDef: IElementDefinition = {
  renderer(props) { return <MentionElement {...props} /> },
  setup(service) {
    const { isVoid, isInline } = service.editor
    service.editor.isVoid = (el) => el.type === 'mention' ? true : isVoid(el)
    service.editor.isInline = (el) => el.type === 'mention' ? true : isInline(el)
  },
}

const useStyles = makeStyles({
  mentionWrapper: {
    cursor: 'pointer',
    background: '#EEE',
    borderRadius: 4,
    padding: '2px 6px',
  },
  mentionFocused: {
    boxShadow: '0 0 0 3px #B4D5FF',
  },
  mentionPopover: {
    marginTop: 12,
  },
})
